import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import {
  getServices,
  createService,
  updateService,
  deleteServiceById,
  setCreateService,
  setUpdateService,
  setDeleteServiceById,
} from '../../redux/actions/service';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import ServiceModal from './subcomponents/ServiceModal';
import { hasPermission } from '../../utils/permissions';

function ServiceList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.service.get);
  const createState = useSelector((state) => state.service.create);
  const updateState = useSelector((state) => state.service.update);
  const deleteState = useSelector((state) => state.service.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getServices());
  }, [dispatch]);

  const handleCreate = useCallback(
    (formData) => {
      setLoading(true);
      dispatch(createService(formData));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, formData) => {
      setLoading(true);
      dispatch(updateService(id, formData));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteServiceById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingService(null);
    setIsEditModalOpen(false);
  };

  const onServiceCreate = (formData) => {
    handleCreate(formData);
    closeCreateModal();
  };

  const onServiceEdit = (formData) => {
    if (editingService) {
      handleEdit(editingService.id, formData);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Servicios';
    // Solo carga inicial
    setLoading(true);
    dispatch(getServices());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getServices());
      }
      clearReduxState(setCreateService);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getServices());
      }
      clearReduxState(setUpdateService);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getServices());
      }
      clearReduxState(setDeleteServiceById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('services.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear servicio</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Servicios</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Imagen</th>
              <th>Título</th>
              <th>Subtítulo</th>
              <th>Precio</th>
              <th>Categoría</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center">
                  No hay servicios disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    {row.imagen_url ? (
                      <img
                        src={row.imagen_url}
                        alt={row.titulo}
                        style={{ maxWidth: '100px', maxHeight: '60px' }}
                      />
                    ) : (
                      'Sin imagen'
                    )}
                  </td>
                  <td>{row.titulo}</td>
                  <td>{row.subtitulo}</td>
                  <td>{row.precio}</td>
                  <td>
                    <span
                      className={`badge ${
                        row.categoria === 'Venta de Bienes'
                          ? 'badge-primary'
                          : row.categoria === 'Propiedades'
                          ? 'badge-success'
                          : 'badge-info'
                      }`}
                    >
                      {row.categoria}
                    </span>
                  </td>
                  <td>{row.active ? 'Sí' : 'No'}</td>
                  <td>
                    <>
                      {hasPermission('services.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('services.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger"
                          iconClass="fa fa-trash"
                          title="Eliminar servicio"
                          message="¿Estas seguro que quieres eliminar este servicio? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Service Modal */}
      <ServiceModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear servicio"
        onSubmit={onServiceCreate}
      />

      {/* Edit Service Modal */}
      <ServiceModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar servicio"
        defaultValue={editingService}
        onSubmit={onServiceEdit}
      />
    </>
  );
}

export default ServiceList;
